/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue } from "recoil";
import { UserAPI } from "../../../api/User";

import { UserAtom, UserCustomCopyTemplates } from "../../../atoms/UserAtom";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import { useModal } from "../Modal";

const ModalBody = styled.div`
  max-width: 400px;
  min-width: 400px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  border-top: 1px solid #eceeee;

  padding: 24px;
  margin: 18px -22px -25px;
`;

const Buttons = styled.span`
  width: calc(100% + 48px);

  display: flex;
  justify-content: flex-end;
  align-items: center;

  border-top: 1px solid #eceeee;

  padding: 24px 24px 0;
`;

const InputLabel = styled.div`
  width: 100%;

  margin-bottom: 48px;
  margin-top: 24px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  > input::placeholder {
    font-size: 16px;
    color: #979797;
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2f2f2f;

  margin-bottom: 16px;
`;

interface CreateCustomCopyModalProps {
  setSelectedTemplate: Dispatch<SetStateAction<string>>;
}

export default function CreateCustomCopyModal(
  props: CreateCustomCopyModalProps
): JSX.Element {
  const modal = useModal();

  const [templateCopyArray, setTemplateCopyArray] = useRecoilState(
    UserCustomCopyTemplates
  );
  const user = useRecoilValue(UserAtom);

  const [isLoading, setIsLoading] = useState(false);
  const [templateName, setTemplateName] = useState<string>("");

  async function handleCreateTemplate() {
    const templateItemDetected = templateCopyArray.find(
      (item) => item.title === templateName
    );

    if (templateItemDetected?.title) {
      toast("Template already exists", {
        type: "error",
      });
      return;
    }

    const templateItem = {
      userUid: user.userUid,
    };

    setIsLoading(true);

    try {
      const response = await UserAPI.createCustomCopyTemplate({
        title: templateName,
        template: "",
      });

      setTemplateCopyArray((prevArray) => [...prevArray, response]);
      props.setSelectedTemplate(response.title);

      toast("Template created successfully", {
        type: "success",
      });
    } catch (err) {
      toast("Failed to create template", {
        type: "error",
      });
    } finally {
      setIsLoading(false);
      modal.popModal();
    }
  }

  return (
    <ModalBody>
      <InputLabel>
        <Title>Template name</Title>
        <StyledInput
          placeholder="Enter template name"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />
      </InputLabel>

      <Buttons>
        <StyledButton
          onClick={() => modal.popModal()}
          inverted
          css={css`
            width: 80px;
            margin-right: 10px;
          `}
        >
          Cancel
        </StyledButton>
        <StyledButton
          isLoading={isLoading}
          disable={!templateName.trim()}
          onClick={handleCreateTemplate}
          css={css`
            width: 80px;
          `}
        >
          Save
        </StyledButton>
      </Buttons>
    </ModalBody>
  );
}
